import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { MenuItem } from '@mui/material';
import {
  Archive,
  CalendarDays,
  CheckCircle2,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Eye,
  ExternalLink,
  ListChecks,
  Loader2,
  Plus,
  Scale,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  Utensils,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import { RecipePortionSizesSection } from '@/components/recipes/RecipePortionSizesSection';
import { RecipeTagsSection } from '@/components/recipes/RecipeTagsSection';
import { toast } from '@/hooks/use-toast';
import { useRecipePortionSizes } from '@/hooks/recipes/useRecipePortionSizes';
import {
  COOKING_METHOD_OPTIONS,
  DISH_TYPE_OPTIONS,
  MEAL_TYPE_OPTIONS,
} from '@/constants/dietary-goals';
import {
  mergeRecipeAllergens,
  mergeRecipeIntolerances,
  normalizeRecipeAllergenList,
  normalizeRecipeIntoleranceList,
} from '@/utils/recipe-allergens';
import {
  deleteAIRecipeArchive,
  getAIRecipeArchive,
  listAIRecipeArchive,
  reviewAIRecipeArchive,
  searchInternalIngredientMatches,
  type AIRecipeIngredientMapping,
  type AIRecipeAdditionalIngredient,
  type AIRecipeInstructionEdit,
  type AIRecipeEditableFields,
  type AIRecipeArchiveDocument,
  type AIRecipeArchiveListItem,
  type AIRecipeArchiveRecord,
  type InternalIngredientMatch,
} from '@/lib/api/aiRecipeArchive';

const PAGE_SIZE = 25;

function formatDate(value?: string | null, includeTime = false): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(parsed);
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(displayValue).join(', ') || '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value).replace(/_/g, ' ');
}

function positiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, '');
}

const UNIT_LABELS: Record<string, { singular: string; plural: string }> = {
  g: { singular: 'g', plural: 'g' },
  gram: { singular: 'g', plural: 'g' },
  grams: { singular: 'g', plural: 'g' },
  kg: { singular: 'kg', plural: 'kg' },
  ml: { singular: 'ml', plural: 'ml' },
  l: { singular: 'L', plural: 'L' },
  tsp: { singular: 'tsp', plural: 'tsp' },
  teaspoon: { singular: 'tsp', plural: 'tsp' },
  tbsp: { singular: 'tbsp', plural: 'tbsp' },
  tablespoon: { singular: 'tbsp', plural: 'tbsp' },
  cup: { singular: 'cup', plural: 'cups' },
  piece: { singular: 'piece', plural: 'pieces' },
  slice: { singular: 'slice', plural: 'slices' },
  bowl: { singular: 'bowl', plural: 'bowls' },
  serving: { singular: 'serving', plural: 'servings' },
};

function normalizeServingUnit(value: unknown): string | null {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized || ['unit', 'unknown', 'none', 'n_a', 'na', '-'].includes(normalized)) return null;
  return normalized;
}

function formatAmountWithUnit(amount: number, rawUnit: string): string {
  const normalized = rawUnit.toLowerCase();
  const configured = UNIT_LABELS[normalized];
  const fallback = rawUnit.replace(/_/g, ' ');
  const unit = configured
    ? (amount === 1 ? configured.singular : configured.plural)
    : (amount === 1 || fallback.endsWith('s') ? fallback : `${fallback}s`);
  return `${formatNumber(amount)} ${unit}`;
}

function formatWeight(value: unknown): string | null {
  const grams = positiveNumber(value);
  return grams == null ? null : `${formatNumber(grams)} g`;
}

function getIngredientDisplay(ingredient: AIRecipeArchiveRecord): {
  portion: string;
  weight: string | null;
  cookedWeight: string | null;
  note: string | null;
} {
  const amount = positiveNumber(ingredient.amount ?? ingredient.quantity ?? ingredient.value);
  const servingId = normalizeServingUnit(ingredient.servingId ?? ingredient.unit ?? ingredient.amountUnit);
  const weight = formatWeight(ingredient.amountGrams ?? ingredient.weightGrams ?? ingredient.weight);
  const cookedWeight = formatWeight(ingredient.cookedAmountGrams);
  const unitIsWeight = servingId != null && ['g', 'gram', 'grams'].includes(servingId);

  let portion = 'Quantity not recorded';
  if (amount != null && servingId) {
    portion = formatAmountWithUnit(amount, servingId);
  } else if (weight) {
    portion = weight;
  }

  return {
    portion,
    weight: unitIsWeight && amount != null ? null : weight,
    cookedWeight,
    note: String(ingredient.preparationNote ?? ingredient.notes ?? '').trim() || null,
  };
}

function getRecipeServingSizes(recipe: AIRecipeArchiveRecord): Array<{
  id: string;
  label: string;
  grams: string | null;
  ml: string | null;
  isDefault: boolean;
}> {
  const rawSizes: AIRecipeArchiveRecord[] = Array.isArray(recipe.servingSizes) ? recipe.servingSizes : [];
  const sizes = rawSizes.map((serving, index) => {
    const amount = positiveNumber(serving.quantity) ?? 1;
    const servingId = normalizeServingUnit(serving.servingId ?? serving.id ?? serving.label) ?? 'serving';
    return {
      id: String(serving.servingId ?? serving.id ?? index),
      label: serving.label
        ? `${formatNumber(amount)} ${String(serving.label).trim()}`
        : formatAmountWithUnit(amount, servingId),
      grams: formatWeight(serving.grams ?? serving.gramsOverride ?? serving.weightG),
      ml: positiveNumber(serving.ml) == null ? null : `${formatNumber(positiveNumber(serving.ml)!)} ml`,
      isDefault: serving.isDefault === true || servingId === normalizeServingUnit(recipe.defaultServingId),
    };
  });
  if (sizes.length > 0) return sizes;

  const defaultGrams = formatWeight(recipe.defaultServingGrams);
  const defaultId = normalizeServingUnit(recipe.defaultServingId);
  return defaultGrams || defaultId ? [{
    id: defaultId ?? 'default',
    label: defaultId ? formatAmountWithUnit(1, defaultId) : 'Default serving',
    grams: defaultGrams,
    ml: null,
    isDefault: true,
  }] : [];
}

function getRecipeName(recipe: AIRecipeArchiveDocument | null): string {
  return String(recipe?.recipe?.name || recipe?.recipe?.title || 'Archived AI recipe');
}

const DetailField: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div className="rounded-xl border border-[#e1ebef] bg-[#f8fbfc] p-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#71838b]">{label}</p>
    <p className="mt-1 break-words text-sm font-medium text-[#17262f]">{displayValue(value)}</p>
  </div>
);

const Metric: React.FC<{
  label: string;
  value: string | number;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  bar: string;
}> = ({ label, value, detail, icon: Icon, accent, bar }) => (
  <div className="relative overflow-hidden rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
    <span className={`absolute inset-x-0 top-0 h-1 ${bar}`} />
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6b7c85]">{label}</p>
        <p className="mt-2 text-3xl font-bold text-[#12303a]">{value}</p>
      </div>
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <p className="mt-3 text-sm text-[#5d6f78]">{detail}</p>
  </div>
);

interface IngredientMappingDraft {
  rowKey: string;
  originalIngredientIndex: number | null;
  query: string;
  matches: InternalIngredientMatch[];
  internalIngredientId: string;
  quantity: string;
  unit: string;
  loading: boolean;
}

interface InstructionDraft {
  rowKey: string;
  originalInstructionIndex: number | null;
  instruction: string;
}

interface RecipeFieldDraft {
  name: string;
  aliases: string;
  description: string;
  cookingTips: string;
  servings: string;
  prepTimeMinutes: string;
  cookTimeMinutes: string;
  cuisines: string[];
  mealTypes: string[];
  dishTypes: string[];
  cookingMethod: string;
  dietLabels: string[];
}

const normalizeMatchName = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const normalizeRecipeOptionList = (value: unknown, separator: '_' | '-' = '_'): string[] => (
  Array.isArray(value)
    ? Array.from(new Set(value.map((item) => String(item ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, separator)
      .replace(new RegExp(`\\${separator}+`, 'g'), separator)
      .replace(new RegExp(`^\\${separator}|\\${separator}$`, 'g'), ''))
      .filter(Boolean)))
    : []
);

const IngredientMappingReview: React.FC<{
  archive: AIRecipeArchiveDocument;
  onReviewed: () => Promise<void>;
}> = ({ archive, onReviewed }) => {
  const ingredients = useMemo(
    () => Array.isArray(archive.ingredients) ? archive.ingredients : [],
    [archive.ingredients],
  );
  const instructions = useMemo(
    () => Array.isArray(archive.instructions) ? archive.instructions : [],
    [archive.instructions],
  );
  const recipe = useMemo(
    () => archive.recipe && typeof archive.recipe === 'object' ? archive.recipe : {},
    [archive.recipe],
  );
  const [drafts, setDrafts] = useState<IngredientMappingDraft[]>([]);
  const [instructionDrafts, setInstructionDrafts] = useState<InstructionDraft[]>([]);
  const [recipeDraft, setRecipeDraft] = useState<RecipeFieldDraft>({
    name: '',
    aliases: '',
    description: '',
    cookingTips: '',
    servings: '1',
    prepTimeMinutes: '',
    cookTimeMinutes: '',
    cuisines: [],
    mealTypes: [],
    dishTypes: [],
    cookingMethod: '',
    dietLabels: [],
  });
  const [manualAllergens, setManualAllergens] = useState<string[]>([]);
  const [manualIntoleranceTags, setManualIntoleranceTags] = useState<string[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const portionConfig = useRecipePortionSizes(recipeDraft.dishTypes);
  const hydrateFromServingSizesRef = useRef(portionConfig.hydrateFromServingSizes);

  useEffect(() => {
    hydrateFromServingSizesRef.current = portionConfig.hydrateFromServingSizes;
  }, [portionConfig.hydrateFromServingSizes]);

  useEffect(() => {
    let cancelled = false;
    const existingMappings = Array.isArray(archive.ingredientMappings) ? archive.ingredientMappings : [];
    const initial = ingredients.map((ingredient, index) => {
      const existing = existingMappings[index]
        ?? existingMappings.find((mapping) => mapping.ingredientIndex === index);
      const name = String(ingredient.name ?? ingredient.ingredientName ?? '');
      return {
        rowKey: `original-${index}`,
        originalIngredientIndex: index,
        query: name,
        matches: existing ? [{
          id: existing.internalIngredientId,
          name: existing.internalIngredientName || 'Mapped internal ingredient',
          allergens: existing.allergens ?? ingredient.allergens ?? [],
          allergenTagIds: existing.allergenTagIds ?? ingredient.allergenTagIds ?? [],
          intoleranceTags: existing.intoleranceTags ?? ingredient.intoleranceTags ?? [],
          intoleranceTagIds: existing.intoleranceTagIds ?? ingredient.intoleranceTagIds ?? [],
        }] : [],
        internalIngredientId: existing?.internalIngredientId ?? '',
        quantity: existing?.quantity != null
          ? String(existing.quantity)
          : String(ingredient.amount ?? ingredient.quantity ?? ingredient.amountGrams ?? ''),
        unit: existing?.unit ?? String(ingredient.servingId ?? ingredient.unit ?? ingredient.amountUnit ?? 'g'),
        loading: !existing,
      };
    });
    setDrafts(initial);

    void Promise.all(initial.map(async (draft, index) => {
      if (draft.internalIngredientId || draft.query.trim().length < 2) return null;
      const matches = await searchInternalIngredientMatches(draft.query);
      return { index, matches };
    })).then((results) => {
      if (cancelled) return;
      setDrafts((current) => current.map((draft, index) => {
        const result = results.find((item) => item?.index === index);
        if (!result) return { ...draft, loading: false };
        const aiName = normalizeMatchName(ingredients[index]?.name ?? ingredients[index]?.ingredientName);
        const exact = result.matches.find((match) =>
          normalizeMatchName(match.displayName ?? match.name) === aiName
        );
        return {
          ...draft,
          matches: result.matches,
          internalIngredientId: exact?.id ?? draft.internalIngredientId,
          loading: false,
        };
      }));
    }).catch(() => {
      if (!cancelled) setDrafts((current) => current.map((draft) => ({ ...draft, loading: false })));
    });

    return () => { cancelled = true; };
  }, [archive.id, archive.ingredientMappings, ingredients]);

  useEffect(() => {
    setInstructionDrafts(instructions.map((instruction, index) => ({
      rowKey: `instruction-${index}`,
      originalInstructionIndex: index,
      instruction: String(instruction.instruction ?? instruction.text ?? instruction.description ?? ''),
    })));
  }, [archive.id, instructions]);

  useEffect(() => {
    setRecipeDraft({
      name: String(recipe.name ?? recipe.title ?? ''),
      aliases: Array.isArray(recipe.aliases) ? recipe.aliases.join(', ') : '',
      description: String(recipe.description ?? ''),
      cookingTips: String(recipe.cookingTips ?? ''),
      servings: String(recipe.servings ?? 1),
      prepTimeMinutes: recipe.prepTimeMinutes == null ? '' : String(recipe.prepTimeMinutes),
      cookTimeMinutes: recipe.cookTimeMinutes == null ? '' : String(recipe.cookTimeMinutes),
      cuisines: normalizeRecipeOptionList(recipe.cuisines),
      mealTypes: normalizeRecipeOptionList(recipe.mealTypes),
      dishTypes: normalizeRecipeOptionList(recipe.dishTypes, '-').slice(0, 1),
      cookingMethod: normalizeRecipeOptionList([recipe.cookingMethod])[0] ?? '',
      dietLabels: normalizeRecipeOptionList(recipe.dietLabels),
    });
    setManualAllergens(normalizeRecipeAllergenList(recipe.manualAllergens ?? []));
    setManualIntoleranceTags(normalizeRecipeIntoleranceList(recipe.manualIntoleranceTags ?? []));
    hydrateFromServingSizesRef.current({
      servingSizes: Array.isArray(recipe.servingSizes) ? recipe.servingSizes : [],
      defaultServingId: recipe.defaultServingId ?? null,
      defaultServingGrams: recipe.defaultServingGrams ?? null,
      dishTypesOverride: normalizeRecipeOptionList(recipe.dishTypes, '-').slice(0, 1),
    });
  }, [archive.id, recipe]);

  const selectedIngredientMetadata = useMemo(() => drafts.flatMap((draft) => {
    const match = draft.matches.find((candidate) => candidate.id === draft.internalIngredientId);
    return match ? [match] : [];
  }), [drafts]);
  const ingredientDerivedAllergens = useMemo(
    () => mergeRecipeAllergens(...selectedIngredientMetadata.flatMap((ingredient) => [
      ingredient.allergens ?? [],
      ingredient.allergenTagIds ?? [],
    ])),
    [selectedIngredientMetadata],
  );
  const ingredientDerivedIntolerances = useMemo(
    () => mergeRecipeIntolerances(...selectedIngredientMetadata.flatMap((ingredient) => [
      ingredient.intoleranceTags ?? [],
      ingredient.intoleranceTagIds ?? [],
    ])),
    [selectedIngredientMetadata],
  );
  const displayedAllergens = useMemo(
    () => mergeRecipeAllergens(manualAllergens, ingredientDerivedAllergens),
    [ingredientDerivedAllergens, manualAllergens],
  );
  const displayedIntolerances = useMemo(
    () => mergeRecipeIntolerances(manualIntoleranceTags, ingredientDerivedIntolerances),
    [ingredientDerivedIntolerances, manualIntoleranceTags],
  );

  const updateDraft = (index: number, patch: Partial<IngredientMappingDraft>) => {
    setDrafts((current) => current.map((draft, draftIndex) =>
      draftIndex === index ? { ...draft, ...patch } : draft
    ));
  };

  const addIngredient = () => {
    setDrafts((current) => [...current, {
      rowKey: `added-${Date.now()}-${current.length}`,
      originalIngredientIndex: null,
      query: '',
      matches: [],
      internalIngredientId: '',
      quantity: '',
      unit: 'g',
      loading: false,
    }]);
  };

  const removeIngredient = (index: number) => {
    setDrafts((current) => current.filter((_draft, draftIndex) => draftIndex !== index));
  };

  const addInstruction = () => {
    setInstructionDrafts((current) => [...current, {
      rowKey: `added-instruction-${Date.now()}-${current.length}`,
      originalInstructionIndex: null,
      instruction: '',
    }]);
  };

  const updateInstruction = (index: number, instruction: string) => {
    setInstructionDrafts((current) => current.map((draft, draftIndex) => (
      draftIndex === index ? { ...draft, instruction } : draft
    )));
  };

  const removeInstruction = (index: number) => {
    setInstructionDrafts((current) => current.filter((_draft, draftIndex) => draftIndex !== index));
  };

  const updateRecipeDraft = (patch: Partial<RecipeFieldDraft>) => {
    setRecipeDraft((current) => ({ ...current, ...patch }));
  };

  const parseCommaSeparated = (value: string): string[] => value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  const handleAllergensChange = (values: string[]) => {
    const locked = new Set(ingredientDerivedAllergens);
    setManualAllergens(normalizeRecipeAllergenList(values).filter((value) => !locked.has(value)));
  };

  const handleIntolerancesChange = (values: string[]) => {
    const locked = new Set(ingredientDerivedIntolerances);
    setManualIntoleranceTags(normalizeRecipeIntoleranceList(values).filter((value) => !locked.has(value)));
  };

  const buildServingSizes = () => {
    const selectedServingId = String(portionConfig.portionSizeId ?? '').trim().toLowerCase();
    const seen = new Set<string>();
    return portionConfig.portionSizes.flatMap((portion, index) => {
      const servingId = portionConfig.getPortionOptionValue(portion, index);
      const grams = portionConfig.resolvePortionGrams(portion);
      if (!servingId || seen.has(servingId) || grams == null || grams <= 0) return [];
      seen.add(servingId);
      const ml = portionConfig.resolvePortionMl(portion);
      const density = Number(portion.densityGPerMl);
      const quantity = Number(portion.quantity);
      return [{
        servingId,
        grams: Math.round(grams * 100) / 100,
        ...(ml != null ? { ml: Math.round(ml * 100) / 100 } : {}),
        ...(Number.isFinite(density) && density > 0
          ? { densityGPerMl: Math.round(density * 10_000) / 10_000 }
          : {}),
        ...(Number.isFinite(quantity) && quantity > 0
          ? { quantity: Math.round(quantity * 100) / 100 }
          : { quantity: 1 }),
        ...(String(portion.name ?? '').trim() ? { label: String(portion.name).trim() } : {}),
        isDefault: servingId === selectedServingId,
      }];
    });
  };

  const searchMatches = async (index: number) => {
    const draft = drafts[index];
    if (!draft || draft.query.trim().length < 2) return;
    updateDraft(index, { loading: true, internalIngredientId: '' });
    try {
      updateDraft(index, { matches: await searchInternalIngredientMatches(draft.query), loading: false });
    } catch (error) {
      updateDraft(index, { loading: false });
      toast({ title: 'Ingredient search failed', description: axios.isAxiosError(error) ? error.message : 'Unable to search internal ingredients.', variant: 'destructive' });
    }
  };

  const complete = drafts.length > 0 && drafts.every((draft) =>
    Boolean(draft.internalIngredientId && draft.unit.trim() && Number(draft.quantity) > 0)
  ) && instructionDrafts.every((draft) => Boolean(draft.instruction.trim()))
    && Boolean(recipeDraft.name.trim())
    && Number(recipeDraft.servings) > 0
    && (recipeDraft.prepTimeMinutes === '' || Number(recipeDraft.prepTimeMinutes) >= 0)
    && (recipeDraft.cookTimeMinutes === '' || Number(recipeDraft.cookTimeMinutes) >= 0)
    && Boolean(portionConfig.portionSizeId)
    && Number(portionConfig.defaultServingGrams) > 0
    && buildServingSizes().length > 0;

  const approveRecipe = async () => {
    if (!complete) return;
    const mappings: AIRecipeIngredientMapping[] = drafts
      .filter((draft) => draft.originalIngredientIndex != null)
      .map((draft) => ({
      ingredientIndex: draft.originalIngredientIndex,
      internalIngredientId: draft.internalIngredientId,
      quantity: Number(draft.quantity),
      unit: draft.unit.trim(),
    }));
    const retainedIndexes = new Set(mappings.map((mapping) => mapping.ingredientIndex));
    const removedIngredientIndexes = ingredients
      .map((_ingredient, index) => index)
      .filter((index) => !retainedIndexes.has(index));
    const additionalIngredients: AIRecipeAdditionalIngredient[] = drafts
      .filter((draft) => draft.originalIngredientIndex == null)
      .map((draft) => ({
        internalIngredientId: draft.internalIngredientId,
        quantity: Number(draft.quantity),
        unit: draft.unit.trim(),
      }));
    const reviewedInstructions: AIRecipeInstructionEdit[] = instructionDrafts.map((draft) => ({
      originalInstructionIndex: draft.originalInstructionIndex,
      instruction: draft.instruction.trim(),
    }));
    const servingSizes = buildServingSizes();
    const defaultServingId = String(portionConfig.portionSizeId).trim().toLowerCase();
    const defaultServing = servingSizes.find((serving) => serving.servingId === defaultServingId);
    if (!defaultServing) {
      toast({ title: 'Portion size required', description: 'Select a default portion with a valid weight before approving.', variant: 'destructive' });
      return;
    }
    const recipeUpdates: AIRecipeEditableFields = {
      name: recipeDraft.name.trim(),
      aliases: parseCommaSeparated(recipeDraft.aliases),
      description: recipeDraft.description.trim() || null,
      cookingTips: recipeDraft.cookingTips.trim() || null,
      servings: Number(recipeDraft.servings),
      prepTimeMinutes: recipeDraft.prepTimeMinutes === '' ? null : Number(recipeDraft.prepTimeMinutes),
      cookTimeMinutes: recipeDraft.cookTimeMinutes === '' ? null : Number(recipeDraft.cookTimeMinutes),
      cuisines: recipeDraft.cuisines,
      mealTypes: recipeDraft.mealTypes,
      dishTypes: recipeDraft.dishTypes,
      cookingMethod: recipeDraft.cookingMethod || null,
      dietLabels: recipeDraft.dietLabels,
      manualAllergens,
      manualIntoleranceTags,
      defaultServingId,
      defaultServingGrams: defaultServing.grams,
      servingSizes,
    };
    setReviewing(true);
    try {
      await reviewAIRecipeArchive(archive.id, {
        ingredientMappings: mappings,
        removedIngredientIndexes,
        additionalIngredients,
        instructions: reviewedInstructions,
        recipeUpdates,
      });
      toast({ title: archive.reviewed ? 'AI recipe updated' : 'AI recipe reviewed', description: 'Recipe fields were saved and nutrition was recalculated from the final ingredient list.' });
      await onReviewed();
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to approve this AI recipe.';
      toast({ title: 'Review failed', description: message, variant: 'destructive' });
    } finally {
      setReviewing(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#bfe4d2] bg-[#f5fcf8] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-[#167a55]" />
            <h3 className="font-semibold text-[#17262f]">Internal ingredient approval</h3>
            <Badge className={archive.reviewed ? 'bg-[#167a55] text-white' : 'bg-[#fff3d6] text-[#8a5b08]'}>
              {archive.reviewed ? 'Reviewed' : 'Not reviewed'}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-[#5d717a]">Edit recipe details and match every ingredient. Reviewed recipes can be updated again.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={addIngredient}>
            <Plus className="mr-2 h-4 w-4" /> Add ingredient row
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/ingredients" target="_blank"><ExternalLink className="mr-2 h-4 w-4" /> Add missing ingredient to database</Link>
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 rounded-xl border border-[#d5e9de] bg-white p-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="md:col-span-2 lg:col-span-3">
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Recipe name</p>
          <Input value={recipeDraft.name} onChange={(event) => updateRecipeDraft({ name: event.target.value })} />
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Servings</p>
          <Input type="number" min="0.1" step="any" value={recipeDraft.servings} onChange={(event) => updateRecipeDraft({ servings: event.target.value })} />
        </div>
        <div className="md:col-span-2 lg:col-span-4">
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Aliases (comma-separated)</p>
          <Input value={recipeDraft.aliases} onChange={(event) => updateRecipeDraft({ aliases: event.target.value })} placeholder="Alternative recipe names" />
        </div>
        <div className="md:col-span-2 lg:col-span-4">
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Description</p>
          <Textarea value={recipeDraft.description} onChange={(event) => updateRecipeDraft({ description: event.target.value })} className="min-h-20" />
        </div>
        <div>
          <AppOutlinedTextField
            select
            fullWidth
            size="small"
            label="Dish Type"
            value={recipeDraft.dishTypes[0] ?? ''}
            onChange={(event) => updateRecipeDraft({ dishTypes: event.target.value ? [String(event.target.value)] : [] })}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">Not specified</MenuItem>
            {DISH_TYPE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
          </AppOutlinedTextField>
        </div>
        <div>
          <AppOutlinedTextField
            select
            fullWidth
            size="small"
            label="Meal Types"
            value={recipeDraft.mealTypes}
            onChange={(event) => {
              const value = event.target.value;
              updateRecipeDraft({ mealTypes: typeof value === 'string' ? value.split(',') : value as string[] });
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => {
                const values = selected as string[];
                if (values.length === 0) return <span className="text-slate-400">Not specified</span>;
                return MEAL_TYPE_OPTIONS.filter((option) => values.includes(option.value)).map((option) => option.name).join(', ');
              },
            }}
          >
            {MEAL_TYPE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
          </AppOutlinedTextField>
        </div>
        <div>
          <AppOutlinedTextField
            select
            fullWidth
            size="small"
            label="Cooking Method"
            value={recipeDraft.cookingMethod}
            onChange={(event) => updateRecipeDraft({ cookingMethod: String(event.target.value ?? '') })}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">Not specified</MenuItem>
            {COOKING_METHOD_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>)}
          </AppOutlinedTextField>
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Prep time (minutes)</p>
          <Input type="number" min="0" value={recipeDraft.prepTimeMinutes} onChange={(event) => updateRecipeDraft({ prepTimeMinutes: event.target.value })} />
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Cook time (minutes)</p>
          <Input type="number" min="0" value={recipeDraft.cookTimeMinutes} onChange={(event) => updateRecipeDraft({ cookTimeMinutes: event.target.value })} />
        </div>
        <div className="md:col-span-2 lg:col-span-4">
          <p className="mb-1 text-xs font-semibold text-[#71838b]">Cooking tips</p>
          <Textarea value={recipeDraft.cookingTips} onChange={(event) => updateRecipeDraft({ cookingTips: event.target.value })} className="min-h-20" />
        </div>
      </div>

      <div className="relative z-30 mt-4 rounded-xl border border-[#d5e9de] bg-white p-4">
        <div className="mb-4">
          <h4 className="font-semibold text-[#17262f]">Tags, allergens and intolerances</h4>
          <p className="mt-1 text-xs text-[#5d717a]">
            Allergens and intolerances detected from selected internal ingredients appear automatically and cannot be removed here. Managers can add or remove manual tags.
          </p>
        </div>
        <RecipeTagsSection
          allergies={displayedAllergens}
          intoleranceTags={displayedIntolerances}
          cuisineTypes={recipeDraft.cuisines}
          dietLabels={recipeDraft.dietLabels}
          lockedAllergies={ingredientDerivedAllergens}
          lockedIntoleranceTags={ingredientDerivedIntolerances}
          onAllergiesChange={handleAllergensChange}
          onIntoleranceTagsChange={handleIntolerancesChange}
          onCuisineTypesChange={(cuisines) => updateRecipeDraft({ cuisines })}
          onDietLabelsChange={(dietLabels) => updateRecipeDraft({ dietLabels })}
        />
      </div>

      <div className="relative z-20 mt-4 rounded-xl border border-[#d5e9de] bg-white p-4">
        <div className="mb-4">
          <h4 className="font-semibold text-[#17262f]">Portion sizes</h4>
          <p className="mt-1 text-xs text-[#5d717a]">Edit the default portion, weights, volume conversion, and custom portions before approval.</p>
        </div>
        <RecipePortionSizesSection
          portionSizeId={portionConfig.portionSizeId}
          portionSizes={portionConfig.portionSizes}
          defaultServingGrams={portionConfig.defaultServingGrams}
          portionDensityGPerMl={portionConfig.portionDensityGPerMl}
          customPortionName={portionConfig.customPortionName}
          customPortionGrams={portionConfig.customPortionGrams}
          customPortionMl={portionConfig.customPortionMl}
          onPortionSizeChange={portionConfig.setPortionSizeId}
          onDefaultServingGramsChange={portionConfig.setDefaultServingGrams}
          onPortionDensityChange={portionConfig.setPortionDensityGPerMl}
          onPortionWeightChange={portionConfig.setPortionWeight}
          onPortionMlChange={portionConfig.setPortionVolume}
          onPortionQuantityChange={portionConfig.setPortionQuantity}
          onCustomPortionNameChange={portionConfig.setCustomPortionName}
          onCustomPortionGramsChange={portionConfig.setCustomPortionGrams}
          onCustomPortionMlChange={portionConfig.setCustomPortionMl}
          onAddCustomPortion={portionConfig.addCustomPortion}
          onRemovePortion={portionConfig.removePortion}
          getPortionOptionValue={portionConfig.getPortionOptionValue}
        />
      </div>

      <div className="mt-4 space-y-3">
        {drafts.map((draft, index) => {
          const ingredient = draft.originalIngredientIndex == null
            ? null
            : ingredients[draft.originalIngredientIndex];
          return (
            <div key={draft.rowKey} className="rounded-xl border border-[#d5e9de] bg-white p-4">
              <div className="grid gap-3 lg:grid-cols-[minmax(12rem,0.8fr)_minmax(15rem,1.2fr)_8rem_8rem_auto]">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#71838b]">
                    {ingredient ? 'AI ingredient' : 'Manager-added ingredient'}
                  </p>
                  <p className="mt-1 font-semibold text-[#17262f]">
                    {ingredient ? displayValue(ingredient.name ?? ingredient.ingredientName) : (draft.query || 'New ingredient')}
                  </p>
                </div>
                <div>
                  <div className="flex gap-2">
                    <Input value={draft.query} onChange={(event) => updateDraft(index, { query: event.target.value, internalIngredientId: '', matches: [] })} placeholder="Type any ingredient name" />
                    <Button type="button" variant="outline" size="sm" onClick={() => void searchMatches(index)} disabled={draft.loading || draft.query.trim().length < 2}>
                      {draft.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1 h-4 w-4" /> Search</>}
                    </Button>
                  </div>
                  <Select value={draft.internalIngredientId} onValueChange={(value) => updateDraft(index, { internalIngredientId: value })}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder={draft.loading ? 'Finding matches...' : 'Select a database match'} /></SelectTrigger>
                    <SelectContent>
                      {draft.matches.map((match) => <SelectItem key={match.id} value={match.id}>{match.displayName || match.name}{match.sourceId ? ` (${match.sourceId})` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!draft.loading && draft.matches.length === 0 ? (
                    <p className="mt-1 text-xs text-[#a15c13]">
                      No internal match found.{' '}
                      <Link className="font-semibold underline" to={`/ingredients?name=${encodeURIComponent(draft.query)}`} target="_blank">Add this ingredient</Link>, then search again.
                    </p>
                  ) : null}
                  {!draft.loading && draft.matches.length > 0 ? (
                    <p className="mt-1 text-xs text-[#5d717a]">None of these match? Enter a different name above and search all internal ingredients again.</p>
                  ) : null}
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold text-[#71838b]">Quantity</p>
                  <Input type="number" min="0" step="any" value={draft.quantity} onChange={(event) => updateDraft(index, { quantity: event.target.value })} />
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold text-[#71838b]">Unit</p>
                  <Input value={draft.unit} onChange={(event) => updateDraft(index, { unit: event.target.value })} placeholder="g, cup..." />
                </div>
                <div className="flex items-end">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeIngredient(index)} aria-label="Remove ingredient">
                    <Trash2 className="h-4 w-4 text-[#b33a3a]" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {drafts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#d5e9de] bg-white p-5 text-center text-sm text-[#71838b]">
            Add at least one ingredient before approving this recipe.
          </div>
        ) : null}
      </div>

      <div className="mt-6 border-t border-[#d5e9de] pt-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="font-semibold text-[#17262f]">Recipe instructions</h4>
            <p className="text-xs text-[#5d717a]">Edit, remove, reorder by deleting and re-adding, or add preparation steps before approval.</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addInstruction}>
            <Plus className="mr-2 h-4 w-4" /> Add instruction
          </Button>
        </div>
        <div className="mt-3 space-y-3">
          {instructionDrafts.map((draft, index) => (
            <div key={draft.rowKey} className="flex items-start gap-3 rounded-xl border border-[#d5e9de] bg-white p-3">
              <span className="mt-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eaf6fa] text-xs font-bold text-[#167f9d]">{index + 1}</span>
              <Textarea
                value={draft.instruction}
                onChange={(event) => updateInstruction(index, event.target.value)}
                placeholder="Describe this recipe step"
                className="min-h-20"
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeInstruction(index)} aria-label="Remove instruction">
                <Trash2 className="h-4 w-4 text-[#b33a3a]" />
              </Button>
            </div>
          ))}
          {instructionDrafts.length === 0 ? <p className="rounded-xl border border-dashed border-[#d5e9de] bg-white p-4 text-center text-sm text-[#71838b]">No instructions. Add a step if this recipe needs one.</p> : null}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-[#5d717a]">{drafts.filter((draft) => draft.internalIngredientId).length} of {drafts.length} final ingredients mapped</p>
        <Button onClick={() => void approveRecipe()} disabled={!complete || reviewing} className="bg-[#167a55] hover:bg-[#106342]">
          {reviewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          {archive.reviewed ? 'Save reviewed recipe changes' : 'Approve recipe'}
        </Button>
      </div>
    </section>
  );
};

const RecipeDetailDialog: React.FC<{
  archive: AIRecipeArchiveDocument | null;
  loading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewed: () => Promise<void>;
}> = ({ archive, loading, open, onOpenChange, onReviewed }) => {
  const recipe = archive?.recipe ?? {};
  const ingredients = Array.isArray(archive?.ingredients) ? archive.ingredients : [];
  const instructions = Array.isArray(archive?.instructions) ? archive.instructions : [];
  const nutrition = recipe.nutritionPerServing && typeof recipe.nutritionPerServing === 'object'
    ? recipe.nutritionPerServing as Record<string, unknown>
    : {};
  const servingSizes = getRecipeServingSizes(recipe);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto p-0">
        <DialogHeader className="border-b border-[#d8e8ed] bg-gradient-to-r from-[#effafe] via-white to-[#f3fbf8] px-6 py-5 text-left">
          <DialogTitle className="pr-8 text-2xl text-[#12303a]">{getRecipeName(archive)}</DialogTitle>
          <DialogDescription>
            Complete immutable snapshot captured from the AI meal-plan archive.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex min-h-72 items-center justify-center gap-3 text-[#57707a]">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading complete recipe snapshot...
          </div>
        ) : archive ? (
          <div className="space-y-6 px-6 py-5">
            <IngredientMappingReview archive={archive} onReviewed={onReviewed} />
            <section className="rounded-2xl border border-[#dbe8ed] bg-gradient-to-br from-[#f8fcfd] to-white p-5">
              <h3 className="text-sm font-bold uppercase tracking-[0.06em] text-[#45606b]">Archive provenance</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <DetailField label="Report date" value={archive.reportDate} />
                <DetailField label="Archived at" value={formatDate(archive.archivedAt as string, true)} />
                <DetailField label="Source recipe ID" value={archive.sourceRecipeId} />
                <DetailField label="Source draft ID" value={archive.sourceDraftId} />
                <DetailField label="Draft" value={archive.draftName} />
                <DetailField label="Client ID" value={archive.clientId} />
                <DetailField label="Nutritionist ID" value={archive.nutritionistId} />
                <DetailField label="Selected in plan" value={archive.selected} />
                <DetailField label="Day" value={archive.dayNumber} />
                <DetailField label="Day date" value={formatDate(archive.dayDate as string)} />
                <DetailField label="Meal" value={archive.mealName} />
                <DetailField label="Meal type" value={archive.mealType} />
              </div>
            </section>

            <section className="rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <ChefHat className="h-5 w-5 text-[#167f9d]" />
                <h3 className="font-semibold text-[#17262f]">Recipe</h3>
              </div>
              {recipe.description ? <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-[#4b626c]">{String(recipe.description)}</p> : null}
              {recipe.cookingTips ? <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#4b626c]"><span className="font-semibold">Cooking tips:</span> {String(recipe.cookingTips)}</p> : null}
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <DetailField label="Aliases" value={recipe.aliases} />
                <DetailField label="Servings" value={recipe.servings} />
                <DetailField label="Prep time" value={recipe.prepTimeMinutes ?? recipe.preparationMinutes} />
                <DetailField label="Cook time" value={recipe.cookTimeMinutes ?? recipe.cookingMinutes} />
                <DetailField label="Total time" value={recipe.totalMinutes ?? recipe.readyInMinutes} />
                <DetailField label="Dish types" value={recipe.dishTypes} />
                <DetailField label="Cooking method" value={recipe.cookingMethod} />
                <DetailField label="Cuisines" value={recipe.cuisines} />
                <DetailField label="Meal types" value={recipe.mealTypes} />
                <DetailField label="Diet labels" value={recipe.dietLabels} />
                <DetailField label="Allergens" value={recipe.allergens} />
                <DetailField label="Intolerances" value={recipe.intoleranceTags} />
              </div>
              <div className="mt-5 border-t border-[#e5eef1] pt-4">
                <div className="flex items-center gap-2">
                  <Scale className="h-4 w-4 text-[#167f9d]" />
                  <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5d717a]">Portion sizes and weights</p>
                </div>
                {servingSizes.length > 0 ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {servingSizes.map((serving) => (
                      <div key={serving.id} className={`rounded-xl border p-3 ${serving.isDefault ? 'border-[#7dcfe2] bg-[#eef9fc]' : 'border-[#dbe8ed] bg-[#fafcfd]'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold capitalize text-[#17262f]">{serving.label.replace(/_/g, ' ')}</p>
                          {serving.isDefault ? <Badge className="bg-[#167f9d] text-white hover:bg-[#167f9d]">Default</Badge> : null}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs font-medium text-[#4b626c]">
                          {serving.grams ? <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-[#d3e5ea]">Weight {serving.grams}</span> : null}
                          {serving.ml ? <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-[#d3e5ea]">Volume {serving.ml}</span> : null}
                          {!serving.grams && !serving.ml ? <span>Weight not recorded</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="mt-2 text-sm text-[#71838b]">No recipe-level portion weights were stored.</p>}
              </div>
            </section>

            <section className="rounded-2xl border border-[#dbe8ed] bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Utensils className="h-5 w-5 text-[#167f9d]" />
                <h3 className="font-semibold text-[#17262f]">Ingredients ({ingredients.length})</h3>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {ingredients.length > 0 ? ingredients.map((ingredient, index) => {
                  const display = getIngredientDisplay(ingredient);
                  return (
                    <div key={String(ingredient._id || index)} className="relative overflow-hidden rounded-2xl border border-[#dbe8ed] bg-[#fbfdfe] p-4">
                      <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#2eb4e4] to-[#167f9d]" />
                      <div className="flex items-start gap-3 pl-1">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eaf6fa] text-xs font-bold text-[#167f9d]">{index + 1}</span>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[#17262f]">{displayValue(ingredient.name || ingredient.ingredientName)}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="rounded-full bg-[#dff3f8] px-3 py-1 text-xs font-bold text-[#126f89]">Portion {display.portion}</span>
                            {display.weight ? <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#4b626c] ring-1 ring-[#d3e5ea]">Weight {display.weight}</span> : null}
                            {display.cookedWeight ? <span className="rounded-full bg-[#fff8e7] px-3 py-1 text-xs font-semibold text-[#8a5b08]">Cooked {display.cookedWeight}</span> : null}
                          </div>
                          {display.note ? <p className="mt-2 text-xs leading-5 text-[#6b7c85]">Preparation: {display.note}</p> : null}
                          {ingredient.originalText && ingredient.originalText !== ingredient.name ? <p className="mt-1 text-xs italic text-[#82949c]">Source: {displayValue(ingredient.originalText)}</p> : null}
                        </div>
                      </div>
                    </div>
                  );
                }) : <p className="text-sm text-[#6b7c85]">No ingredient rows were captured.</p>}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-[#167f9d]" />
                <h3 className="font-semibold text-[#17262f]">Instructions ({instructions.length})</h3>
              </div>
              <ol className="mt-3 space-y-3">
                {instructions.length > 0 ? instructions
                  .slice()
                  .sort((a, b) => Number(a.stepNumber ?? 0) - Number(b.stepNumber ?? 0))
                  .map((instruction, index) => (
                    <li key={String(instruction._id || index)} className="flex gap-3 rounded-2xl border border-[#dbe8ed] p-4">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eaf6fa] text-xs font-bold text-[#167f9d]">
                        {instruction.stepNumber ?? index + 1}
                      </span>
                      <p className="whitespace-pre-wrap text-sm leading-6 text-[#344c56]">{displayValue(instruction.instruction ?? instruction.text ?? instruction.description)}</p>
                    </li>
                  )) : <p className="text-sm text-[#6b7c85]">No instruction rows were captured.</p>}
              </ol>
            </section>

            <section>
              <h3 className="font-semibold text-[#17262f]">Nutrition per serving</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {Object.keys(nutrition).length > 0
                  ? Object.entries(nutrition).map(([key, value]) => <DetailField key={key} label={key.replace(/_/g, ' ')} value={value} />)
                  : <p className="text-sm text-[#6b7c85]">No nutrition snapshot was captured.</p>}
              </div>
            </section>

            <details className="rounded-2xl border border-[#dbe8ed] bg-[#f8fbfc] p-4">
              <summary className="cursor-pointer text-sm font-semibold text-[#167f9d]">View complete stored archive JSON</summary>
              <pre className="mt-4 max-h-[32rem] overflow-auto whitespace-pre-wrap break-all rounded-xl bg-[#10252d] p-4 text-xs leading-5 text-[#d9eef4]">
                {JSON.stringify(archive, null, 2)}
              </pre>
            </details>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const AIRecipeReviewPage: React.FC = () => {
  const [items, setItems] = useState<AIRecipeArchiveListItem[]>([]);
  const [search, setSearch] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'selected' | 'not_selected'>('all');
  const [reviewedFilter, setReviewedFilter] = useState<'all' | 'reviewed' | 'not_reviewed'>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState<AIRecipeArchiveDocument | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AIRecipeArchiveListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadArchive = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listAIRecipeArchive({
        page,
        pageSize: PAGE_SIZE,
        q: search.trim() || undefined,
        reportDate: reportDate || undefined,
        selected: selectedFilter,
        reviewed: reviewedFilter,
      });
      setItems(response.items);
      setTotalCount(response.pagination.totalCount);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to load the AI recipe archive.';
      toast({ title: 'Archive failed to load', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, reportDate, reviewedFilter, search, selectedFilter]);

  useEffect(() => { void loadArchive(); }, [loadArchive]);

  const openDetails = async (id: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedArchive(null);
    try {
      setSelectedArchive(await getAIRecipeArchive(id));
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to load the complete recipe snapshot.';
      toast({ title: 'Recipe failed to load', description: message, variant: 'destructive' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshSelectedArchive = async () => {
    if (!selectedArchive) return;
    setSelectedArchive(await getAIRecipeArchive(selectedArchive.id));
    await loadArchive();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAIRecipeArchive(deleteTarget.id);
      toast({
        title: 'Archive entry deleted',
        description: 'The archived copy was removed. The source meal plan and live AI recipe were not changed.',
      });
      setDeleteTarget(null);
      await loadArchive();
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error || error.message
        : 'Unable to delete this archive entry.';
      toast({ title: 'Delete failed', description: message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const selectedOnPage = useMemo(() => items.filter((item) => item.selected).length, [items]);
  const pageRange = totalCount === 0
    ? '0'
    : `${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, totalCount)}`;

  return (
    <main className="min-h-[calc(100vh-7.5rem)] rounded-[28px] border border-[#dbe6eb] bg-white px-4 py-5 sm:px-6 lg:px-8">
      <div className="space-y-5">
        <header className="relative overflow-hidden rounded-2xl border border-[#d8e8ed] bg-gradient-to-r from-[#f0fafd] via-white to-[#f2fbf7] p-5 sm:p-6">
          <span className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-[#2eb4e4] to-[#167f9d]" />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Badge variant="outline" className="border-[#bfe4ed] bg-[#f5fbfd] text-[#167f9d]">Recipe manager review</Badge>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-[#12303a] sm:text-3xl">AI recipe review</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-[#4b626c]">
              Match AI-generated ingredients to the internal database and approve recipes for future use.
            </p>
          </div>
          <Button variant="outline" onClick={() => void loadArchive()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-3">
          <Metric label="Archived recipes" value={totalCount} detail="Matching the current filters" icon={Archive} accent="bg-[#eaf6fa] text-[#167f9d]" bar="bg-gradient-to-r from-[#2eb4e4] to-[#167f9d]" />
          <Metric label="Shown on page" value={items.length} detail="Recipe snapshots visible now" icon={Sparkles} accent="bg-[#f0ecff] text-[#6d52b5]" bar="bg-gradient-to-r from-[#9d83e6] to-[#6d52b5]" />
          <Metric label="Selected recipes" value={selectedOnPage} detail="Chosen meal-plan recipes on this page" icon={ChefHat} accent="bg-[#eefbf5] text-[#167a55]" bar="bg-gradient-to-r from-[#4fc58c] to-[#167a55]" />
        </div>

        <section className="overflow-hidden rounded-2xl border border-[#dbe8ed] shadow-sm">
          <div className="grid gap-3 border-b border-[#e4eef2] bg-[#fbfdfe] p-4 md:grid-cols-[minmax(15rem,1fr)_12rem_11rem_11rem_auto]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#78909a]" />
              <Input
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                placeholder="Search recipe, ingredient, meal or draft"
                className="pl-9"
              />
            </div>
            <div className="relative">
              <CalendarDays className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-[#78909a]" />
              <Input
                type="date"
                value={reportDate}
                onChange={(event) => { setReportDate(event.target.value); setPage(1); }}
                className="pl-9"
                aria-label="Report date"
              />
            </div>
            <Select value={selectedFilter} onValueChange={(value) => { setSelectedFilter(value as typeof selectedFilter); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plan options</SelectItem>
                <SelectItem value="selected">Selected recipes</SelectItem>
                <SelectItem value="not_selected">Alternatives</SelectItem>
              </SelectContent>
            </Select>
            <Select value={reviewedFilter} onValueChange={(value) => { setReviewedFilter(value as typeof reviewedFilter); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All review states</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="not_reviewed">Not reviewed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={() => { setSearch(''); setReportDate(''); setSelectedFilter('all'); setReviewedFilter('all'); setPage(1); }}>
              Clear
            </Button>
          </div>

          {loading ? (
            <div className="flex min-h-64 items-center justify-center gap-3 text-[#57707a]">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading archived recipes...
            </div>
          ) : items.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center">
              <Archive className="h-10 w-10 text-[#9bb0b8]" />
              <p className="mt-3 font-semibold text-[#344c56]">No archived AI recipes match these filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#edf3f5]">
              {items.map((item) => (
                <article key={item.id} className="grid gap-4 px-4 py-4 transition-colors hover:bg-[#f7fcfd] sm:px-6 lg:grid-cols-[minmax(0,1fr)_12rem_10rem_auto] lg:items-center">
                  <div className="min-w-0">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#eaf6fa] text-[#167f9d]"><Utensils className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate font-semibold text-[#17262f]">{item.recipeName}</h2>
                      {item.selected ? <Badge className="bg-[#e9f8f1] text-[#167a55] hover:bg-[#e9f8f1]">Selected</Badge> : <Badge variant="secondary">Alternative</Badge>}
                      {item.reviewed ? <Badge className="bg-[#167a55] text-white hover:bg-[#167a55]">Reviewed</Badge> : <Badge className="bg-[#fff3d6] text-[#8a5b08] hover:bg-[#fff3d6]">Not reviewed</Badge>}
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm text-[#5d717a]">{item.description || 'No description captured.'}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#71838b]">
                      <span>{item.ingredientCount} ingredients</span>
                      <span>{item.instructionCount} steps</span>
                      <span>{item.mealName || item.mealType || 'Meal not labelled'}</span>
                      <span>Day {item.dayNumber ?? '-'}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#82949c]">Report date</p>
                    <p className="mt-1 text-sm font-medium text-[#344c56]">{formatDate(item.reportDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[#82949c]">Archived</p>
                    <p className="mt-1 text-sm text-[#344c56]">{formatDate(item.archivedAt, true)}</p>
                  </div>
                  <div className="flex gap-2 lg:justify-end">
                    <Button variant="outline" size="sm" onClick={() => void openDetails(item.id)}>
                      <Eye className="mr-2 h-4 w-4" /> View fully
                    </Button>
                    <Button variant="outline" size="sm" className="border-[#efc9c9] text-[#b42318] hover:bg-[#fff4f3]" onClick={() => setDeleteTarget(item)}>
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete {item.recipeName}</span>
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}

          <footer className="flex flex-col gap-3 border-t border-[#e4eef2] bg-[#fbfdfe] px-4 py-3 text-sm text-[#5d717a] sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <span>Showing {pageRange} of {totalCount}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <span className="px-2 text-xs font-semibold">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </footer>
        </section>
      </div>

      <RecipeDetailDialog
        archive={selectedArchive}
        loading={detailLoading}
        open={detailOpen}
        onOpenChange={(open) => { setDetailOpen(open); if (!open) setSelectedArchive(null); }}
        onReviewed={refreshSelectedArchive}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this archived recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the archived occurrence of <strong>{deleteTarget?.recipeName}</strong>. It does not delete the source meal plan or live AI recipe. The deletion actor and identifiers will remain in an audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} onClick={(event) => { event.preventDefault(); void confirmDelete(); }} className="bg-[#b42318] hover:bg-[#8f1c14]">
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete archive entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default AIRecipeReviewPage;
